import * as React from "react";
import { cloneDeep, debounce, groupBy } from "lodash";
import { OutlinedInput, FormGroup, FormControlLabel, Checkbox } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { CopyIconText } from "../Filters/constants";
import { SelectLabel } from "../../components/Filters";
import QuestionnaireAccordion from "../QuestionnaireAccordion";
import { getLatestAnswer, isLatestAnswerEmpty, isLatestAnswerUnverified } from "../../utils/answerUtils";
import moment from "moment";

function Search({ onSearchKeywordChange, disabled, noResult, activeSearchKey }) {
  const [searchInput, setSearchInput] = React.useState("");

  React.useEffect(() => {
    if (activeSearchKey !== searchInput) {
      setSearchInput(activeSearchKey);
    }
  }, [activeSearchKey]);

  async function handleChange(e) {
    setSearchInput(e.target.value);
    onSearchKeywordChange(e.target.value);
  }

  return (
    <>
      <OutlinedInput
        sx={{ maxHeight: "40px" }}
        fullWidth
        placeholder="Search"
        endAdornment={<SearchIcon />}
        onChange={handleChange}
        value={searchInput}
        disabled={disabled}
      />
      {noResult && searchInput.length > 0 ? <span>No Question or Answer found</span> : null}
    </>
  );
}

function FilterCheckbox({ disabled, onChange, label, activeChecked }) {
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    if (activeChecked !== checked) {
      setChecked(activeChecked);
    }
  }, [activeChecked]);

  function handleChange(e) {
    setChecked(e.target.checked);
    onChange(e.target.checked);
  }

  return (
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={handleChange} />}
      label={
        label === "Insert Using Formatted Text" && Office.context.host === Office.HostType.PowerPoint ? (
          <SelectLabel label={label} tooltipText={CopyIconText} />
        ) : (
          label
        )
      }
      sx={{ color: "#444444" }}
      disabled={disabled}
    />
  );
}

export default function Questionnaire({ tab, bidDetails }) {
  const [sections, setSections] = React.useState([]);
  const [filteredSections, setFilteredSections] = React.useState([]);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [expandAll, setExpandAll] = React.useState(false);
  const [filterUnanswered, setFilterUnanswered] = React.useState(false);
  const [filterUnverified, setFilterUnverified] = React.useState(false);
  const [formattedText, setFormattedText] = React.useState(false);

  React.useEffect(() => {
    let updatedSections = [];
    let updatedFilteredSections = [];
    let sortedQuestions = [];
    if (bidDetails && Array.isArray(bidDetails.proposalQuestions)) {
      sortedQuestions = cloneDeep(bidDetails.proposalQuestions).sort(
        (questionA, questionB) => questionA.questionOrder - questionB.questionOrder
      );
    }
    const questionsMap = sortedQuestions.reduce((acc, question) => {
      acc[question.questionId] = cloneDeep(question);
      return acc;
    }, {});

    // based on tab, filter questions and group them by sections
    if (tab !== "") {
      switch (tab) {
        case "Strategy Development": {
          Object.entries(groupBy(sortedQuestions, (question) => question.section.sectionName)).forEach(
            ([sectionName, questions]: any) => {
              let filteredQuestions = questions.filter(
                (question) =>
                  question.questionApproval !== true &&
                  question.active === true &&
                  !isLatestAnswerEmpty(question.answers) &&
                  !isLatestAnswerUnverified(question.answers)
              );

              if (Office?.context?.host === Office?.HostType?.PowerPoint) {
                filteredQuestions = filteredQuestions.filter(
                  (question: any) => question.answerConfiguration.type !== "table"
                );
              }

              updatedSections.push({
                sectionName,
                questions: questions,
                expanded: false,
                sectionOrder: questions[0].section.sectionOrder,
              });

              if (filteredQuestions.length > 0) {
                updatedFilteredSections.push({
                  sectionName,
                  questions: filteredQuestions,
                  expanded: false,
                  sectionOrder: filteredQuestions[0].section.sectionOrder,
                });
              }
            }
          );

          break;
        }
        case "Approvals": {
          if (Array.isArray(bidDetails.proposalApproval)) {
            bidDetails.proposalApproval.forEach((approvalSection) => {
              const questionsForSection = approvalSection.ApprovalSectionLeftQuestions.concat(
                approvalSection.ApprovalSectionRightQuestions
              )
                .map((questionId) => questionsMap[questionId] || null)
                .filter((question) => !!question)
                .sort((questionA, questionB) => questionA.questionOrder - questionB.questionOrder);

              let filteredQuestionsForSection = questionsForSection.filter(
                (question) =>
                  question.active === true &&
                  !isLatestAnswerEmpty(question.answers) &&
                  !isLatestAnswerUnverified(question.answers)
              );

              if (Office?.context?.host === Office?.HostType?.PowerPoint) {
                filteredQuestionsForSection = filteredQuestionsForSection.filter(
                  (question: any) => question.answerConfiguration.type !== "table"
                );
              }

              const archivedDataLength = approvalSection.ArchivedData.length;
              const approvalSectionName =
                archivedDataLength > 0
                  ? approvalSection.ApprovalSectionTitle + " " + `${archivedDataLength + 1}`
                  : approvalSection.ApprovalSectionTitle;
              if (questionsForSection.length > 0) {
                updatedSections.push({
                  expanded: false,
                  sectionName: approvalSectionName,
                  questions: questionsForSection,
                });
              }

              if (filteredQuestionsForSection.length > 0) {
                updatedFilteredSections.push({
                  expanded: false,
                  sectionName: approvalSectionName,
                  questions: filteredQuestionsForSection,
                });
              }
            });
          }
          break;
        }
        default:
          if (bidDetails.proposal && Array.isArray(bidDetails.proposal.customUnityTabs)) {
            const customTabSections = bidDetails.proposal.customUnityTabs.filter(
              (customTab) => customTab.UnityTabId === tab
            );
            if (customTabSections.length > 0) {
              customTabSections.forEach((section) => {
                const questionsForSection = section.UnityTabSectionQuestions.map(
                  (questionId) => questionsMap[questionId] || null
                )
                  .filter((question) => !!question)
                  .sort((questionA, questionB) => questionA.questionOrder - questionB.questionOrder);
                let filteredQuestionsForSection = questionsForSection.filter(
                  (question) =>
                    question.active === true &&
                    !isLatestAnswerEmpty(question.answers) &&
                    !isLatestAnswerUnverified(question.answers)
                );

                if (Office?.context?.host === Office?.HostType?.PowerPoint) {
                  filteredQuestionsForSection = filteredQuestionsForSection.filter(
                    (question: any) => question.answerConfiguration.type !== "table"
                  );
                }

                if (questionsForSection.length > 0) {
                  updatedSections.push({
                    expanded: false,
                    sectionName: section.UnityTabSectionTitle,
                    questions: questionsForSection,
                  });
                }

                if (filteredQuestionsForSection.length > 0) {
                  updatedFilteredSections.push({
                    expanded: false,
                    sectionName: section.UnityTabSectionTitle,
                    questions: filteredQuestionsForSection,
                  });
                }
              });
            }
          }
          break;
      }
    }

    updatedSections = updatedSections.sort(
      (sectionA: any, sectionB: any) => sectionA.sectionOrder - sectionB.sectionOrder
    );
    updatedFilteredSections = updatedFilteredSections.sort(
      (sectionA: any, sectionB: any) => sectionA.sectionOrder - sectionB.sectionOrder
    );

    setSections(updatedSections);
    setFilteredSections(updatedFilteredSections);
    setSearchKeyword("");
    setExpandAll(false);
    setFormattedText(false);
    setFilterUnanswered(false);
    setFilterUnverified(false);
  }, [tab, bidDetails]);

  React.useEffect(() => {
    let updatedFilteredSections = cloneDeep(sections);
    let filteredQuestions = [];
    const sectionsExpandedMap = {};
    updatedFilteredSections.forEach((section, sectionOrder) => {
      filteredQuestions = filteredQuestions.concat(
        section.questions.map((question) => ({
          ...question,
          section: {
            sectionOrder: sectionOrder + 1,
            sectionName: section.sectionName,
          },
        }))
      );
      if (Office?.context?.host === Office?.HostType?.PowerPoint) {
        filteredQuestions = filteredQuestions.filter((question: any) => question.answerConfiguration.type !== "table");
      }
      sectionsExpandedMap[section.sectionName] = section.expanded;
    });

    const filterQueries = [];
    if (!filterUnanswered) {
      filterQueries.push(isLatestAnswerEmpty);
    }

    if (!filterUnverified) {
      filterQueries.push(isLatestAnswerUnverified);
    }

    filteredQuestions = filteredQuestions.filter(({ answers, active, questionApproval }) => {
      if (tab === "Strategy Development") {
        return active && questionApproval !== true && filterQueries.every((query) => !query(answers));
      } else return active && filterQueries.every((query) => !query(answers));
    });
    updatedFilteredSections = Object.entries(groupBy(filteredQuestions, (question) => question.section.sectionName))
      .map(([sectionName, questions]) => ({
        sectionName,
        questions,
        expanded: sectionsExpandedMap[sectionName] || false,
        sectionOrder: questions[0].section.sectionOrder,
      }))
      .sort((sectionA: any, sectionB: any) => sectionA.sectionOrder - sectionB.sectionOrder);

    if (searchKeyword.length > 0) {
      updatedFilteredSections = updatedFilteredSections
        .map((section) => ({
          ...section,
          questions: section.questions.filter((question) => {
            if (question.questionText.toLowerCase().includes(searchKeyword.toLowerCase())) {
              return true;
            }
            const recentAnswer = getLatestAnswer(question.answers).answer;
            if (Array.isArray(recentAnswer)) {
              return recentAnswer.some((answerChunk) =>
                answerChunk.toLowerCase().includes(searchKeyword.toLowerCase())
              );
            } else if (typeof recentAnswer === "string") {
              if (question.answerConfiguration.type === "proposal_team") {
                const newAnswer = [];
                recentAnswer.split(",").some((answer) => {
                  const split_array = answer.split("(");
                  if (split_array && split_array.length > 0) {
                    newAnswer.push(split_array[0].trim());
                  }
                });
                return newAnswer.some((answerChunk) => answerChunk.toLowerCase().includes(searchKeyword.toLowerCase()));
              } else if (question.answerConfiguration.type === "date") {
                if (moment(recentAnswer).isValid()) {
                  return moment(recentAnswer).format("DD-MMM-YYYY").toLowerCase().includes(searchKeyword.toLowerCase());
                } else {
                  return recentAnswer.toLowerCase().includes(searchKeyword.toLowerCase());
                }
              } else {
                return recentAnswer.toLowerCase().includes(searchKeyword.toLowerCase());
              }
            }
            return false;
          }),
        }))
        .filter((section) => section.questions.length > 0);
    }
    setFilteredSections(updatedFilteredSections);
  }, [searchKeyword, filterUnanswered, filterUnverified]);

  function handleExpandToggle(index, value) {
    setFilteredSections((prevUpdatedSections) =>
      prevUpdatedSections.map((section, idx) => {
        if (index === idx) {
          return { ...section, expanded: value };
        } else {
          return section;
        }
      })
    );
    setSections((prevSections) =>
      prevSections.map((section, idx) => {
        if (index === idx) {
          return { ...section, expanded: value };
        } else {
          return section;
        }
      })
    );
  }

  React.useEffect(() => {
    setSections((prevSections) => prevSections.map((section) => ({ ...section, expanded: expandAll })));
    setFilteredSections((prevSections) => prevSections.map((section) => ({ ...section, expanded: expandAll })));
  }, [expandAll]);

  const handleFormattedTextChange = React.useCallback((value) => {
    setFormattedText(value);
  }, []);

  const handleExpandAllChange = React.useCallback((value) => {
    setExpandAll(value);
  }, []);

  const handleFilterUnansweredChange = React.useCallback((value) => {
    setFilterUnanswered(value);
  }, []);

  const handleFilterUnverifiedChange = React.useCallback((value) => {
    setFilterUnverified(value);
  }, []);

  const debouncedHandleSearchKeywordChange = debounce(async (keyword) => {
    setSearchKeyword(keyword);
  }, 300);

  return (
    <div className="questionnaire-container">
      {bidDetails !== null ? (
        <>
          <Search
            onSearchKeywordChange={debouncedHandleSearchKeywordChange}
            disabled={bidDetails === null}
            noResult={filteredSections.length === 0}
            activeSearchKey={searchKeyword}
          />
          <FormGroup sx={{ mt: "2rem", mb: "1rem" }}>
            {Office?.context?.platform === Office?.PlatformType?.OfficeOnline &&
            Office.context.host === Office.HostType.PowerPoint ? null : (
              <FilterCheckbox
                label="Insert Using Formatted Text"
                onChange={handleFormattedTextChange}
                disabled={bidDetails === null}
                activeChecked={formattedText}
              />
            )}
            <FilterCheckbox
              label="Expand All"
              onChange={handleExpandAllChange}
              disabled={bidDetails === null}
              activeChecked={expandAll}
            />
            <FilterCheckbox
              label="Show Unanswered Questions"
              onChange={handleFilterUnansweredChange}
              disabled={bidDetails === null}
              activeChecked={filterUnanswered}
            />
            <FilterCheckbox
              label="Show Unverified Answers"
              onChange={handleFilterUnverifiedChange}
              disabled={bidDetails === null}
              activeChecked={filterUnverified}
            />
          </FormGroup>
        </>
      ) : null}

      {filteredSections.map((section, idx) => (
        <QuestionnaireAccordion
          key={`questionnaire-accordion=${idx}-${section.expanded}`}
          info={section}
          onExpandToggle={(value) => handleExpandToggle(idx, value)}
          formattedText={formattedText}
        />
      ))}
    </div>
  );
}
